import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'

interface OfferValueDisplayVisibilityOptions {
  discountPercent?: number
  isDiscountPillHidden?: boolean
  shouldDisplayValue?: boolean
  value?: number
}

export function getOfferValueDisplayVisibility({
  discountPercent,
  isDiscountPillHidden = false,
  shouldDisplayValue = true,
  value,
}: OfferValueDisplayVisibilityOptions) {
  return {
    shouldShowValue: shouldDisplayValue && (value ?? 0) > 0 && !!discountPercent,
    shouldShowDiscount: !isDiscountPillHidden &&
      (discountPercent ?? 0) >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE,
  }
}

export function getHotelOfferDisplayedValue(bundledWithFlightsOnly: boolean, price: number, value: number, shouldDisplayValue: boolean, flightPrice?: number, isCharterBundle = false) {
  const bundledPrice = price + (flightPrice || 0)
  const bundledValue = value + (flightPrice || 0)
  const hotelDiscountPercent = getValueOffPercent(value, price)
  // Maldives charter bundles: the flight leg has no discount of its own, so
  // reuse the hotel-only discount instead of diluting it with the flight price.
  const bundledDiscountPercent = isCharterBundle
    ? hotelDiscountPercent
    : getValueOffPercent(bundledValue, bundledPrice)
  const highestDiscountPercent = Math.max(hotelDiscountPercent, bundledDiscountPercent)
  const shouldShowDiscountPercent = !bundledWithFlightsOnly && highestDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  return {
    shouldShowDiscountPercent,
    shouldShowValue: shouldDisplayValue && value > price,
    highestDiscountPercent,
  }
}

type PriceCanBeDisplayed<T> = {
  isBundledFlightPriceDisplayable: T
}

type BundledFlightPrice = PriceCanBeDisplayed<true> & {
  packagedTotal?: number
  packagedMemberTotal?: number
  totalValue?: number
  flightPrice?: number
  shouldShowValue?: boolean
}

type NoBundlePrice = PriceCanBeDisplayed<false>

export function getBundledWithFlightOfferDisplayPricing(bundledWithFlightsOnly: boolean, flightPrice: number, price: number, memberPrice: number, value: number): BundledFlightPrice | NoBundlePrice {
  if (bundledWithFlightsOnly && Number.isFinite(flightPrice)) {
    const totalValue = value + flightPrice
    const packagedTotal = price + flightPrice
    const packagedMemberTotal = memberPrice > 0 ? memberPrice + flightPrice : 0
    return { packagedTotal, packagedMemberTotal, totalValue, flightPrice, isBundledFlightPriceDisplayable: true }
  }
  return { isBundledFlightPriceDisplayable: false }
}
